import { Grid } from '@mui/material';
import React from 'react';
import styles from './styles.module.css';
import { ModelSetPerformance } from 'features/premium-report/api/types';
import {
  filterPerformance,
  portfolioStatisticsChartFilter,
} from 'pages/Cases/PortfolioConstruction/Summary/PerformanceReport';
import { PortfolioStatisticsLineChart } from 'pages/Cases/components/PortfolioStatisticsLineChart';
import { calculateDateRange } from 'features/premium-report/utils';
import { getSlideField } from '../../../utils';
import { Slide } from '../../../_data';
import { SlideSubtitle } from '../slide-subtitle/slide-subtitle';
import { SlideParagraph } from '../slide-paragraph';
import { EditableField } from '../../../components/EditableField';

export type RiskAnalysisProps = [
  {
    field: 'modelSetPerformance';
    label: string;
    value: ModelSetPerformance[];
    type: 'modelSetPerformance';
  },
  {
    field: 'riskAnalysis';
    label: string;
    value: string;
    type: 'riskAnalysis';
  },
  {
    field: 'profile';
    label: string;
    value: number;
    type: 'number';
  },
];

export const RiskAnalysis = React.memo(
  ({
    slide,
    onChangeSlide,
  }: {
    slide: Slide<RiskAnalysisProps>;
    onChangeSlide: (
      slide: Slide<RiskAnalysisProps>,
      content: RiskAnalysisProps,
    ) => void;
  }): React.ReactElement => {
    const modelSetPerformance = getSlideField(
      slide,
      'modelSetPerformance',
    )?.value;
    const profile = getSlideField(slide, 'profile')?.value;
    const option = [modelSetPerformance[0].portfolio_options[0].option];

    const filteredPerformance = filterPerformance(
      modelSetPerformance,
      [],
      option,
      profile,
    );

    // Verificar si hay datos filtrados antes de renderizar
    if (!filteredPerformance || filteredPerformance.length === 0) {
      return (
        <Grid container direction="column" className={styles.container}>
          <Grid item>
            <SlideSubtitle>Risk Analysis</SlideSubtitle>
          </Grid>
          <Grid item>
            <SlideParagraph>No data available for risk analysis</SlideParagraph>
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid container direction="column" className={styles.container}>
        <Grid item>
          <SlideSubtitle>Risk Analysis</SlideSubtitle>
        </Grid>
        <Grid item>
          <SlideParagraph>
            <b>Model vs Benchmark: Standard Deviation</b>
          </SlideParagraph>
          <SlideParagraph>
            {calculateDateRange(
              filteredPerformance,
              'rolling_three_year_stats',
              '3_year_standard_deviation',
              '36',
            )}
          </SlideParagraph>
          <Grid item>
            <PortfolioStatisticsLineChart
              data={portfolioStatisticsChartFilter({
                filteredPerformance: filteredPerformance,
                name: 'rolling_three_year_stats',
                subName: '3_year_standard_deviation',
              })}
              height={300}
              fontSize={14}
              formatChart={{
                unit: '%',
                decimals: 2,
                prefix: '',
                value: 'Standard Deviation',
                label: 'Standard Deviation',
              }}
              isWithAnimation={false}
            />
          </Grid>
        </Grid>
        <Grid item>
          <SlideParagraph>
            <b>Model vs Benchmark: Maximum Drawdown</b>
          </SlideParagraph>
          <SlideParagraph>
            {calculateDateRange(
              filteredPerformance,
              'rolling_three_year_stats',
              '3_year_max_drawdown',
              '36',
            )}
          </SlideParagraph>
          <Grid item>
            <PortfolioStatisticsLineChart
              data={portfolioStatisticsChartFilter({
                filteredPerformance: filteredPerformance,
                name: 'rolling_three_year_stats',
                subName: '3_year_max_drawdown',
              })}
              height={300}
              fontSize={14}
              formatChart={{
                unit: '%',
                decimals: 2,
                prefix: '',
                value: 'Maximum Drawdown',
                label: 'Maximum Drawdown',
              }}
              isWithAnimation={false}
            />
          </Grid>
        </Grid>
        <Grid item>
          <SlideParagraph
            style={{
              marginBottom: '10px',
              marginTop: '10px',
              fontSize: '13px',
            }}
          >
            Source: TIFIN Sage
          </SlideParagraph>
        </Grid>
        <Grid item>
          <SlideSubtitle style={{ marginBottom: '10px', fontSize: '20px' }}>
            Rolling 3-year period risk and drawdown
          </SlideSubtitle>
          <EditableField
            field="riskAnalysis"
            slide={slide}
            onSlideContentChange={onChangeSlide}
            style={{
              fontFamily: 'Roboto',
              fontWeight: '400',
              fontSize: '16px',

              color: '#353536',
            }}
          />
        </Grid>
      </Grid>
    );
  },
);
